import React from "react";
import { Clock, Thermometer, Edit3, AlertCircle, CheckCircle } from "lucide-react";
import { formatDiagnosticDate } from "../../../utils/dateFormatter";

function TempSensStatus({ diagnosticData }) {
  // Extract data safely
  const tempSensData = diagnosticData?.TempSensStatus?.[0] || {};
  const restartData = diagnosticData?.restartStatus?.[0] || {};
  const numberOfTempSensors = parseInt(diagnosticData?.numberOfTempSensors) || 6;
  
  // Format timestamps
  const systemTimestamp = formatDiagnosticDate(tempSensData.SysTimestamp);
  const modificationDate = formatDiagnosticDate(tempSensData.modifiedDate);
  const restartTime = formatDiagnosticDate(restartData.UptimeAtRestart);
  
  // Create sensors array based on the number of temperature sensors
  const sensors = [];
  for (let i = 1; i <= numberOfTempSensors; i++) {
    const sensorKey = `T${i}`;
    sensors.push({
      label: sensorKey,
      value: tempSensData[sensorKey] || "--"
    });
  }
  
  // Calculate overall sensor status
  const allSensorsConnected = sensors.every(sensor => sensor.value === "CONNECTED");
  const sensorStatus = allSensorsConnected ? "All Connected" : "Some Disconnected";
  const statusColor = allSensorsConnected ? "text-green-500" : "text-red-500";
  const statusIcon = allSensorsConnected ? <CheckCircle className={statusColor} size={20} /> : <AlertCircle className={statusColor} size={20} />;
  
  // Determine grid classes based on number of sensors
  const getGridClasses = () => {
    if (numberOfTempSensors === 2) return "grid-cols-2";
    if (numberOfTempSensors === 4) return "grid-cols-2 md:grid-cols-4";
    return "grid-cols-2 md:grid-cols-3";
  };
  
  return (
    <div className="space-y-4">
      {/* Overall Sensor Status */}
      <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500 flex flex-col items-center justify-center text-center">
        <div className="flex items-center justify-center gap-2">
          <Thermometer className="text-blue-500" size={20} />
          <h3 className="text-lg font-bold text-gray-800">Temperature Sensor Status</h3>
        </div>
        <div className="mt-2 flex items-center justify-center gap-2 w-full md:w-[85%] mx-auto px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
          {statusIcon}
          <span className={`text-sm font-medium ${statusColor}`}>{sensorStatus}</span>
        </div>
      </div>
      
      {/* Last Updated Section */}
      <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500 flex flex-col items-center justify-center text-center">
        <div className="flex items-center justify-center gap-2">
          <Clock className="text-blue-500" size={20} />
          <h3 className="text-lg font-bold text-gray-800">Last Updated Date</h3>
        </div>
        <div className="mt-2 w-full md:w-[85%] mx-auto px-3 py-2 border border-gray-300 rounded-lg text-center text-gray-700 bg-gray-50 text-sm font-medium">
          {systemTimestamp}
        </div>
      </div>
      
      {/* Temperature Sensors Section */}
      <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-red-500 flex flex-col items-center justify-center text-center">
        <div className="flex items-center justify-center gap-2">
          <Thermometer className="text-red-500" size={20} />
          <h3 className="text-lg font-bold text-gray-800">Temperature Sensors ({numberOfTempSensors})</h3>
        </div>
        <div className={`grid ${getGridClasses()} gap-3 mt-2 w-full md:w-[85%] mx-auto`}>
          {sensors.map((sensor) => (
            <div
              key={sensor.label}
              className={`px-3 py-2 border border-gray-300 rounded-lg text-center text-sm font-medium
                ${
                  sensor.value === "CONNECTED"
                    ? "text-white border-green-500 bg-green-500 hover:bg-green-600"
                    : "text-white bg-red-500 hover:bg-red-600 border-red-500"
                }`}
            >
              {sensor.label}
            </div>
          ))}
        </div>
      </div>
      
      {/* Restart Time Section */}
      <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-green-500 flex flex-col items-center justify-center text-center">
        <div className="flex items-center justify-center gap-2">
          <Edit3 className="text-green-500" size={20} />
          <h3 className="text-lg font-bold text-gray-800">Last System Restart Time</h3>
        </div>
        <div className="mt-2 w-full md:w-[85%] mx-auto px-3 py-2 border border-gray-300 rounded-lg text-center text-gray-700 bg-gray-50 text-sm font-medium">
          {restartTime}
        </div>
      </div>
    </div>
  );
}


export default TempSensStatus;